import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Auth } from 'aws-amplify';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LoginForm, RegisterForm, ForgotPasswordForm, ResetPasswordForm, AccountLinkingRequest } from '@/types';
import { signInWithGoogle, handleGoogleCallback, GoogleAuthResult } from '@/utils/google-auth';

interface ExtendedRegisterForm extends RegisterForm {
  userType: 'hosts' | 'guests';
}

interface PhoneLoginForm {
  phoneNumber: string;
  password: string;
}

interface PhoneRegisterForm {
  phoneNumber: string;
  password: string;
  confirmPassword: string;
  name: string;
  userType: 'hosts' | 'guests';
}

export const useLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: LoginForm) => {
      const user = await Auth.signIn(data.email, data.password);
      return user;
    },
    onSuccess: () => {
      // Invalidate and refetch user data
      queryClient.invalidateQueries(['user']);
      
      // Redirect to return URL if provided, otherwise stay on homepage
      const returnUrl = searchParams.get('returnUrl');
      const redirectTo = returnUrl && returnUrl !== '/auth/login' ? returnUrl : '/';
      navigate(redirectTo, { replace: true });
    },
  });
};

export const useRegister = () => {
  return useMutation({
    mutationFn: async (data: ExtendedRegisterForm) => {
      // Split the full name into given_name and family_name
      const nameParts = data.name.trim().split(' ');
      const givenName = nameParts[0] || '';
      const familyName = nameParts.slice(1).join(' ') || nameParts[0] || '';

      const { user } = await Auth.signUp({
        username: data.email,
        password: data.password,
        attributes: {
          email: data.email,
          given_name: givenName,
          family_name: familyName,
          'custom:user_group': 'guests' // Always assign guests group initially
        }
      });
      return user;
    },
  });
};

export const useConfirmSignUp = () => {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async ({ email, code }: { email: string; code: string }) => {
      await Auth.confirmSignUp(email, code);
    },
    onSuccess: () => {
      navigate('/auth/login');
    },
  });
};

export const useResendSignUp = () => {
  return useMutation({
    mutationFn: async (email: string) => {
      await Auth.resendSignUp(email);
    },
  });
};

export const useForgotPassword = () => {
  return useMutation({
    mutationFn: async (data: ForgotPasswordForm) => {
      await Auth.forgotPassword(data.email);
      return data.email;
    },
  });
};

export const useResetPassword = () => {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async ({ email, code, newPassword }: { email: string; code: string; newPassword: string }) => {
      await Auth.forgotPasswordSubmit(email, code, newPassword);
    },
    onSuccess: () => {
      navigate('/auth/login');
    },
  });
};

export const useSignOut = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await Auth.signOut();
    },
    onSuccess: () => {
      // Clear all cached data
      queryClient.clear();
      navigate('/');
    },
  });
};

// Google OAuth hooks
export const useGoogleSignIn = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<void> => {
      await signInWithGoogle();
    },
    onSuccess: () => {
      // The actual authentication will be handled by the OAuth callback
      // This just initiates the flow
      console.log('Google OAuth flow initiated');
    },
    onError: (error: any) => {
      console.error('Google sign-in failed:', error);
    },
  });
};

export const useGoogleCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<GoogleAuthResult> => {
      return await handleGoogleCallback();
    },
    onSuccess: (result: GoogleAuthResult) => {
      // Invalidate and refetch user data
      queryClient.invalidateQueries(['user']);
      
      if (result.isNewUser) {
        // Redirect to profile completion for new users
        navigate('/profile?setup=true', { replace: true });
      } else {
        // Redirect to return URL or homepage for existing users
        const returnUrl = searchParams.get('returnUrl');
        const redirectTo = returnUrl && returnUrl !== '/auth/login' ? returnUrl : '/';
        navigate(redirectTo, { replace: true });
      }
    },
  });
};

// Phone number authentication hooks
export const usePhoneLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: PhoneLoginForm) => {
      // Use phone number as username for Cognito
      const user = await Auth.signIn(data.phoneNumber, data.password);
      return user;
    },
    onSuccess: () => {
      // Invalidate and refetch user data
      queryClient.invalidateQueries(['user']);
      
      // Redirect to return URL if provided, otherwise stay on homepage
      const returnUrl = searchParams.get('returnUrl');
      const redirectTo = returnUrl && returnUrl !== '/auth/login' ? returnUrl : '/';
      navigate(redirectTo, { replace: true });
    },
  });
};

export const usePhoneRegister = () => {
  return useMutation({
    mutationFn: async (data: PhoneRegisterForm) => {
      // Split the full name into given_name and family_name
      const nameParts = data.name.trim().split(' ');
      const givenName = nameParts[0] || '';
      const familyName = nameParts.slice(1).join(' ') || nameParts[0] || '';

      const { user } = await Auth.signUp({
        username: data.phoneNumber,
        password: data.password,
        attributes: {
          phone_number: data.phoneNumber,
          given_name: givenName,
          family_name: familyName,
          'custom:user_group': 'guests' // Always assign guests group initially
        }
      });
      return user;
    },
  });
};

export const useConfirmPhoneSignUp = () => {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async ({ phoneNumber, code }: { phoneNumber: string; code: string }) => {
      await Auth.confirmSignUp(phoneNumber, code);
    },
    onSuccess: () => {
      navigate('/auth/login');
    },
  });
};

// Account linking hooks
export const useLinkAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: AccountLinkingRequest) => {
      const user = await Auth.currentAuthenticatedUser();
      
      if (request.linkingMethod === 'google') {
        // Initiate Google OAuth linking
        await Auth.federatedSignIn({ provider: 'Google' as any });
        return { success: true, requiresVerification: false };
      } else {
        // Handle email/phone linking
        if (request.verificationCode) {
          // Verify and link the account
          await Auth.updateUserAttributes(user, {
            [`custom:linked_${request.linkingMethod}`]: request.identifier,
            'custom:auth_methods': JSON.stringify([
              ...JSON.parse(user.attributes['custom:auth_methods'] || '[]'),
              {
                type: request.linkingMethod,
                identifier: request.identifier,
                verified: true,
                primary: false,
                createdAt: new Date().toISOString()
              }
            ])
          });
          return { success: true, requiresVerification: false };
        } else {
          // Send verification code
          if (request.linkingMethod === 'email') {
            // For email, we would typically send a verification email
            // This would be handled by the backend
            return { success: true, requiresVerification: true, verificationSent: true };
          } else if (request.linkingMethod === 'phone') {
            // For phone, send SMS verification
            // This would be handled by the backend
            return { success: true, requiresVerification: true, verificationSent: true };
          }
        }
      }
      
      return { success: false, requiresVerification: false };
    },
    onSuccess: () => {
      // Refresh user data to get updated authentication methods
      queryClient.invalidateQueries(['user']);
    },
  });
};

export const useUnlinkAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (methodType: 'email' | 'phone' | 'google') => {
      const user = await Auth.currentAuthenticatedUser();
      const currentMethods = JSON.parse(user.attributes['custom:auth_methods'] || '[]');
      
      // Remove the specified method
      const updatedMethods = currentMethods.filter((method: any) => method.type !== methodType);
      
      await Auth.updateUserAttributes(user, {
        'custom:auth_methods': JSON.stringify(updatedMethods),
        [`custom:linked_${methodType}`]: '' // Clear the linked identifier
      });
    },
    onSuccess: () => {
      // Refresh user data to get updated authentication methods
      queryClient.invalidateQueries(['user']);
    },
  });
};

